import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { chipSx, doseChipText, clinicalDoseLabel } from '../stack/DoseChip';
import { color, r } from '../../theme/tokens';
import { YOUR_DOSE_LABEL } from './copy';
import { studyDoseLabel } from './doseViews';

/**
 * SplitDoseChip — an analysed row's two doses as ONE chip: "YOUR DOSE 300 mg |
 * STUDIED DOSE 225 mg · once/day" (Analyze Stack engineering handoff,
 * 2026-09-24).
 *
 * **Same shape and size as every other chip on the row** (Tanu, 2026-09-24:
 * elements match the existing stack for uniformity). The handoff draws the
 * labels stacked above 14px values, which makes the chip taller than the dose,
 * studies and products chips beside it; here each half is a single line on
 * `DoseChip`'s own `chipSx`, so border, radius, type and height are identical
 * by construction rather than by copying numbers.
 *
 * Each half is fed the projection built in `doseViews.js`, never the row:
 * the member's carries no provenance, so the clinical-study marker and the
 * frequency can only ever appear on the studied half — the same guarantee the
 * two separate chips gave.
 *
 * The second half reads "Standard dose" instead where the backend held the
 * studied dose to a signed safety limit (`studyDoseLabel`); `DoseChip`'s
 * gating already gives a standard dose no clinical-study marker.
 *
 * On a phone the two halves stack rather than overflow: at 390px the pair is
 * wider than the row, and a chip that pushes past the card is worse than one
 * that is briefly taller.
 */

const labelSx = {
  fontSize: r(10),
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: color.inkSoft,
};

// A half wraps its label, value and marker onto a second line rather than
// clip them. The chip keeps `DoseChip`'s `nowrap` and needs `overflow: hidden`
// for its corners, so without this a studied half wider than a phone's row
// ("225 mg · once/day" and its marker, ~226px against ~202px at 390) was cut
// off with no ellipsis (review, 2026-09-27). `minWidth: 0` lets a half shrink
// below its one-line width in the side-by-side layout too.
const halfSx = {
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: `${r(2)} ${r(6)}`,
  minWidth: 0,
  padding: `${r(6)} ${r(11)}`,
};

const SplitDoseChip = ({ member, study, icon: Icon }) => {
  const memberText = doseChipText(member);
  const studyText = doseChipText(study);
  const studyLabel = clinicalDoseLabel(study);

  if (!memberText && !studyText) return null;

  return (
    <Box
      component="span"
      data-testid="split-dose-chip"
      sx={{
        ...chipSx,
        padding: 0,
        overflow: 'hidden',
        maxWidth: '100%',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
      }}
    >
      {memberText && (
        <Box component="span" sx={halfSx}>
          {Icon && (
            <Icon
              aria-hidden="true"
              // Upright, as on `DoseChip`: the library's capsule is drawn lying down.
              sx={{ fontSize: r(14), flex: 'none', transform: 'rotate(90deg)' }}
            />
          )}
          <Box component="span" sx={labelSx}>{YOUR_DOSE_LABEL}</Box>
          <Box component="span">{memberText}</Box>
        </Box>
      )}

      {studyText && (
        <Box
          component="span"
          sx={{
            ...halfSx,
            backgroundColor: color.surface,
            // The divider between the halves: a side rule in a row, a top rule
            // once they stack. Drawn on the studied half so there is no divider
            // at all when the member half is absent.
            borderLeft: memberText ? { xs: 'none', sm: `${r(1)} solid ${color.line}` } : 'none',
            borderTop: memberText ? { xs: `${r(1)} solid ${color.line}`, sm: 'none' } : 'none',
          }}
        >
          <Box component="span" sx={labelSx}>{studyDoseLabel(study)}</Box>
          <Box component="span">{studyText}</Box>
          {studyLabel && (
            <Tooltip title={studyLabel} arrow placement="top">
              <InfoOutlinedIcon
                aria-label={studyLabel}
                // MUI's SvgIcon sets `aria-hidden` on itself unless told
                // otherwise, and the label then reaches no screen reader. Not
                // `titleAccess`: its SVG <title> is a second, native tooltip
                // under the MUI one. Caller props are spread after MUI's
                // defaults, so this override holds.
                aria-hidden={false}
                role="img"
                sx={{ fontSize: r(12), flex: 'none', color: color.inkMuted }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SplitDoseChip;
